import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useAppUserDefaultStore } from '../../stores/appUserDefaultStore'
import { useRestaurantStore } from '../../stores/appRestaurantStore'
import { useAuth } from '../../services/authentication/authContext'
import { saveDefaultRestaurant, getRestaurantSelectOptions } from '../../services/restaurant/restaurantSelectorService'

/** Shared selector UI; setup controls selection through its route rather than saved app defaults. */
export function RestaurantSelect({ options, selectedId, onChange, label, className = '' }: {
  options: { id: string; name: string }[];
  selectedId: string;
  onChange: (id: string) => void;
  label?: string;
  className?: string;
}) {
  const [selectWidth, setSelectWidth] = useState(200)
  const measureRef = useRef<HTMLSpanElement>(null)
  const selected = options.find(option => option.id === selectedId)

  useEffect(() => {
    if (measureRef.current && selected?.name) {
      const w = measureRef.current.offsetWidth
      setSelectWidth(Math.max(w + 60, 200))
    }
  }, [selected?.name])

  if (!options.length || !selected) return null

  return (
    <div className={`relative inline-block max-w-full ${className}`} style={{ width: `${selectWidth}px` }}>
      <span
        ref={measureRef}
        aria-hidden="true"
        className="absolute invisible whitespace-nowrap text-lg font-medium px-4"
        style={{ top: '-9999px' }}
      >
        {selected.name}
      </span>
      <select
        value={selected.id}
        aria-label={label}
        data-onboarding="restaurant-selector"
        onChange={event => onChange(event.target.value)}
        className="border border-borderGrey dark:border-borderGrey-dark bg-background dark:bg-background-dark text-text dark:text-text-dark px-4 py-3 pr-12 rounded-xl text-lg font-medium hover:shadow-md transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-backgroundGreen/20 dark:focus:ring-backgroundGreen-dark/20 focus:border-backgroundGreen appearance-none max-w-full"
        style={{ width: '100%' }}
      >
        {options.map(option => <option key={option.id} value={option.id}>{option.name}</option>)}
      </select>
      <div className="absolute inset-y-0 right-0 flex items-center pr-4 pointer-events-none">
        <svg className="w-5 h-5 text-text dark:text-text-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>
    </div>
  )
}

const RestaurantSelector: React.FC = () => {
  const restaurants = useRestaurantStore(s => s.restaurants)
  const selected = useAppUserDefaultStore(s => s.restaurant)
  const { backendUser } = useAuth()
  const selectOptions = useMemo(() =>
    backendUser ? getRestaurantSelectOptions(restaurants, backendUser.id) : restaurants,
    [restaurants, backendUser]
  )
  if (!selectOptions.length || !selected) return null

  const handleChange = async (id: string) => {
    const selectedOption = selectOptions.find(r => r.id === id)
    if (!selectedOption) return
    try {
      await saveDefaultRestaurant(selectedOption)
    } catch {
      console.error('Failed to save default restaurant')
    }
  }
  return <RestaurantSelect options={selectOptions} selectedId={selected.id} onChange={handleChange} />
}

export default RestaurantSelector
